import React, { useCallback, useEffect, useMemo } from "react";

import {
  ProductTemplateEntryType,
  useGetProductTemplateConfig,
  useGetProductTemplateData,
} from "api/api-hooks/product-templates-hooks";
import {
  IProductTemplateAttribute,
  ITableDataEntry,
} from "components/dashboard/product-template-table/utils/interfaces";
import { defaultIcon } from "components/pages/measurement-devices/components/ObjectMapContent";
import useTimeState from "hooks/dashboard/useTimeState";
import { LatLngBounds, LatLngTuple } from "leaflet";
import { Marker, Popup, useMap, ZoomControl } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import { ProductItemMapProps } from "../types";

const ProductItemMapContent: React.FC<ProductItemMapProps> = ({
  safeName,
  latitude,
  longitude,
  filter,
  productTemplateId,
  hiddenColumns,
}) => {
  const map = useMap();
  const { from, to } = useTimeState({});
  const params: ProductTemplateEntryType = {
    safeName,
    dateFrom: from?.toISOString(),
    dateTo: to?.toISOString(),
    filter,
  };
  const { data } = useGetProductTemplateData(params);
  const { data: info } = useGetProductTemplateConfig(safeName, productTemplateId?.toString()!, [], [], {
    enabled: Boolean(productTemplateId) && Boolean(safeName),
  });

  const getCoordTuple = useCallback(
    (dataEntry: ITableDataEntry): LatLngTuple | null => {
      const lat = dataEntry.entries?.[latitude]?.value as number;
      const long = dataEntry.entries?.[longitude]?.value as number;
      if (!lat || !long) return null;
      return [lat, long];
    },
    [latitude, longitude]
  );

  const markers = useMemo(
    () =>
      [...(data ?? [])].reduce((tuples, entry) => {
        const tuple = getCoordTuple(entry);
        if (!tuple) return tuples;
        tuples.push(tuple);
        return tuples;
      }, [] as LatLngTuple[]),
    [data, getCoordTuple]
  );

  useEffect(() => {
    if (markers.length === 0)
      map.fitBounds([
        [54.2, 23.5],
        [56.2, 24.5],
      ]);
    if (markers.length === 1) map.setView(markers[0], 16);
    if (markers.length > 1) {
      const bounds = new LatLngBounds(markers);
      const northEast = Object.values(bounds.getNorthEast()) as LatLngTuple;
      const southWest = Object.values(bounds.getSouthWest()) as LatLngTuple;
      map.fitBounds([northEast, southWest]);
    }
  }, [map, markers]);

  const mapMarkers = data?.map(entry => {
    const position = getCoordTuple(entry);
    if (!position) return null;
    return (
      <Marker key={entry.id} position={position} icon={defaultIcon}>
        <Popup>
          <ProductItemPopupContent
            data={entry}
            attributes={info?.data?.attributes ?? []}
            hiddenColumns={hiddenColumns ?? []}
          />
        </Popup>
      </Marker>
    );
  });

  return (
    <>
      <ZoomControl position="bottomright" />
      <MarkerClusterGroup maxClusterRadius={24}>{mapMarkers}</MarkerClusterGroup>
    </>
  );
};

export default ProductItemMapContent;

const ProductItemPopupContent: React.FC<{
  data: ITableDataEntry;
  attributes: IProductTemplateAttribute[];
  hiddenColumns: string[];
}> = ({ data, attributes, hiddenColumns }) => {
  const columnOrder = attributes.map(a => a.referenceName);
  const sortEntries = Object.values(data.entries)
    .sort((a, b) => columnOrder.indexOf(a.referenceName) - columnOrder.indexOf(b.referenceName))
    .filter(entry => !hiddenColumns.includes(entry.referenceName));

  const tableRows = sortEntries.map(entry => {
    const display = attributes.find(att => att.referenceName === entry.referenceName)?.displayName ?? "";
    return (
      <tr key={entry.referenceName}>
        <th>{display}:</th>
        <td className="pl-3">{entry.value?.toString()}</td>
      </tr>
    );
  });

  return (
    <table>
      <tbody>{tableRows}</tbody>
    </table>
  );
};
